import { useState, useEffect, useRef, useCallback } from "react";
import { FetchMoreQueryOptions, FetchMoreOptions } from "@apollo/client";
import { GetReviewByStoreIdQuery } from "../../../../__generated__/graphql";

interface UseInfiniteScrollStoreReviewsOptions {
  storeId: number;
  limit?: number;
  fetchMore:
    | ((
        fetchMoreOptions: FetchMoreQueryOptions<any, any> &
          FetchMoreOptions<any, any>
      ) => Promise<any>)
    | null;
  initialData?: GetReviewByStoreIdQuery["getReviewByStoreId"];
  onInitialDataLoad?: (
    data: GetReviewByStoreIdQuery["getReviewByStoreId"]
  ) => void;
}

export const useInfiniteScrollStoreReviews = ({
  storeId,
  limit = 20,
  fetchMore,
  initialData,
  onInitialDataLoad,
}: UseInfiniteScrollStoreReviewsOptions) => {
  const [allReviews, setAllReviews] = useState<
    GetReviewByStoreIdQuery["getReviewByStoreId"]
  >([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const fetchMoreRef = useRef(fetchMore);

  // fetchMore가 변경될 때마다 ref 업데이트
  useEffect(() => {
    fetchMoreRef.current = fetchMore;
  }, [fetchMore]);

  // 초기 데이터 로드 시 allReviews에 설정
  useEffect(() => {
    if (initialData && initialData.length > 0 && allReviews.length === 0) {
      setAllReviews(initialData);
      setOffset(initialData.length);
      setHasMore(initialData.length === limit);
      onInitialDataLoad?.(initialData);
    }
  }, [initialData, limit, allReviews.length, onInitialDataLoad]);

  // 더 많은 리뷰 불러오기 함수
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !fetchMoreRef.current) return;

    setIsLoadingMore(true);

    try {
      const result = await fetchMoreRef.current({
        variables: {
          storeId,
          limit,
          offset,
        },
      });

      if (result.data?.getReviewByStoreId) {
        const newReviews = result.data.getReviewByStoreId;

        // 중복 제거하여 추가
        setAllReviews((prev) => {
          const existingIds = new Set(prev.map((review) => review?.id));
          const uniqueNewReviews = newReviews.filter(
            (review: GetReviewByStoreIdQuery["getReviewByStoreId"][0]) =>
              review && !existingIds.has(review.id)
          );
          return [...prev, ...uniqueNewReviews];
        });

        setOffset((prev) => prev + newReviews.length);
        setHasMore(newReviews.length === limit);
      }
    } catch (error) {
      console.error("Failed to load more reviews:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, storeId, limit, offset]);

  // Intersection Observer로 무한 스크롤 구현
  useEffect(() => {
    if (!hasMore || isLoadingMore || allReviews.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        rootMargin: "500px",
      }
    );

    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [hasMore, isLoadingMore, handleLoadMore, allReviews.length]);

  return {
    loadMoreRef,
    allReviews,
    setAllReviews,
    hasMore,
    isLoadingMore,
  };
};
